import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  Headphones,
  Server,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { contactAPI } from '../../api';

const KNOWN_SOURCES = new Set(['register', 'pricing', 'hero', 'footer', 'gym_owner']);
const KNOWN_INTENTS = new Set(['gym_owner', 'demo', 'support', 'partnership']);

const resolveLabel = (t, key, fallback) => {
  const translated = t(`landingContactPage.sourceLabels.${key}`);
  return translated && translated !== `landingContactPage.sourceLabels.${key}` ? translated : fallback;
};

const SIGNAL_ICONS = {
  launch: CalendarClock,
  workflow: Headphones,
  deploy: Server,
};

/**
 * /contact — same form (the email pipeline is already working), but
 * the surrounding layout is rewritten to match the new editorial
 * system. Cardless: the form sits inside an editorial panel, the
 * signals sit in a list of editorial rows, the final CTA below
 * closes the page.
 */
export const ContactPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const contactSignals = [
    {
      key: 'launch',
      icon: SIGNAL_ICONS.launch,
      title: t('landingContactPage.signals.launch.title', { defaultValue: 'Launch & rollout' }),
      body: t('landingContactPage.signals.launch.body', {
        defaultValue: 'Pilots, opening dates, and capacity planning for new sites.',
      }),
    },
    {
      key: 'workflow',
      icon: SIGNAL_ICONS.workflow,
      title: t('landingContactPage.signals.workflow.title', { defaultValue: 'Workflow design' }),
      body: t('landingContactPage.signals.workflow.body', {
        defaultValue: 'Points, badges, challenges, and trainer review flows tuned to your team.',
      }),
    },
    {
      key: 'deploy',
      icon: SIGNAL_ICONS.deploy,
      title: t('landingContactPage.signals.deploy.title', { defaultValue: 'Deploy & integration' }),
      body: t('landingContactPage.signals.deploy.body', {
        defaultValue: 'SaaS, EU-hosted single tenant, or on-prem with Keycloak OIDC.',
      }),
    },
  ];

  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
    // Honeypot — real users never fill this. Bots that auto-fill
    // every field will trip the server-side check.
    website: '',
  });

  const sourceKey = useMemo(() => {
    const raw = searchParams.get('source');
    return raw && KNOWN_SOURCES.has(raw) ? raw : 'default';
  }, [searchParams]);

  const intentKey = useMemo(() => {
    const raw = searchParams.get('intent');
    return raw && KNOWN_INTENTS.has(raw) ? raw : 'default';
  }, [searchParams]);

  const sourceLabel = useMemo(
    () => resolveLabel(t, sourceKey, sourceKey),
    [t, sourceKey],
  );
  const intentLabel = useMemo(
    () => resolveLabel(t, intentKey, intentKey),
    [t, intentKey],
  );

  useEffect(() => {
    document.title = t('landing.seo.contactTitle', { defaultValue: 'Contact GymGamify' });
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        t('landing.seo.contactDescription', {
          defaultValue:
            'Talk to the GymGamify team about launches, workflow design, and on-prem deployments. Response within 1 business day.',
        })
      );
    }
  }, [t]);

  const messagePreamble = useMemo(() => {
    if (sourceKey === 'default' && intentKey === 'default') return '';
    return t('landingContactPage.sourceLabels.preamble', { source: sourceLabel, intent: intentLabel });
  }, [t, sourceKey, intentKey, sourceLabel, intentLabel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await contactAPI.submit({
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        source: sourceKey === 'default' ? null : sourceKey,
        intent: intentKey === 'default' ? null : intentKey,
        website: form.website, // honeypot — must be empty
      });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setSubmitError(detail || 'Too many messages. Please try again later.');
      } else {
        setSubmitError(detail || 'Could not deliver your message. Please email hello@gymgamify.app directly.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="layout-shell py-14 lg:py-20">
      <header className="motion-enter max-w-3xl">
        <span className="hero-kicker">
          {t('landingContactPage.kicker', { defaultValue: 'Contact desk' })}
        </span>
        <h1 className="mt-6 editorial-title text-[var(--color-text)]">
          {t('landingContactPage.title', {
            defaultValue: "Let's map your launch together.",
          })}
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-8 text-[var(--color-text-muted)]">
          {t('landingContactPage.description', {
            defaultValue:
              'Tell us about your gym, your launch date, and what success looks like. We read every message and reply within a business day.',
          })}
        </p>
      </header>

      <div className="mt-14 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        {/* Left: what we can help with */}
        <section>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            What we can help with
          </p>
          <ol className="mt-5 space-y-4">
            {contactSignals.map((signal) => (
              <li
                key={signal.key}
                className="editorial-panel motion-hover-lift p-6 md:p-7"
              >
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-primary-200/15 bg-primary-200/10">
                    <signal.icon className="h-4 w-4 text-primary-100" />
                  </span>
                  <div>
                    <h2 className="font-display text-xl uppercase tracking-[0.04em] text-[var(--color-text)]">
                      {signal.title}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-[var(--color-text-muted)]">
                      {signal.body}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-5 px-1 text-[11px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            Or reach us at{' '}
            <a
              href="mailto:hello@gymgamify.app"
              className="text-primary-200 hover:text-primary-100"
            >
              hello@gymgamify.app
            </a>
            .
          </p>
        </section>

        {/* Right: the form panel */}
        <section className="editorial-panel p-6 md:p-8">
          {sent ? (
            <div className="flex min-h-[28rem] flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-12 w-12 text-primary-200" />
              <h2 className="mt-5 font-display text-3xl uppercase tracking-[0.04em] text-[var(--color-text)] md:text-4xl">
                {t('landingContactPage.messageReceived', { defaultValue: 'Message received' })}
              </h2>
              <p className="mt-4 max-w-md text-base leading-7 text-[var(--color-text-muted)]">
                Thanks for reaching out. The team gets an in-app
                notification the moment you submit, and we usually reply
                within a business day.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setForm({ name: '', email: '', message: '', website: '' });
                }}
                className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                Send another message
              </button>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200">
                {t('landingContactPage.contactDesk', {
                  defaultValue: 'Response within 1 business day',
                })}
              </p>
              <h2 className="mt-3 font-display text-3xl uppercase tracking-[0.04em] text-[var(--color-text)] md:text-4xl">
                {t('landingContact.title', { defaultValue: 'Send a message' })}
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-[var(--color-text-muted)]">
                {t('landingContactPage.description', {
                  defaultValue:
                    'Tell us about your gym, your launch date, and what success looks like.',
                })}
              </p>

              {messagePreamble && (
                <div className="mt-6 rounded-2xl border border-primary-200/20 bg-primary-200/5 px-4 py-3 text-xs text-primary-100">
                  <p className="font-semibold uppercase tracking-[0.18em]">
                    {t('landingContactPage.sourceLabels.intentLabel', { defaultValue: 'Inquiry intent' })}
                  </p>
                  <p className="mt-1 leading-5">{messagePreamble}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="label" htmlFor="contact-name">
                    {t('landingContact.form.name', { defaultValue: 'Your name' })}
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    className="input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    autoComplete="name"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="contact-email">
                    {t('landingContact.form.email', { defaultValue: 'Email' })}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    className="input"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    autoComplete="email"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="contact-message">
                    {t('landingContact.form.message', { defaultValue: 'Message' })}
                  </label>
                  <textarea
                    id="contact-message"
                    className="input min-h-[160px]"
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    required
                    disabled={submitting}
                  />
                </div>
                {/* Honeypot: hidden from real users, visible to bots that auto-fill every field */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', height: 0, overflow: 'hidden' }}>
                  <label htmlFor="contact-website">Website (leave blank)</label>
                  <input
                    id="contact-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                  />
                </div>
                {submitError && (
                  <div role="alert" className="rounded-2xl border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-sm text-amber-200">
                    {submitError}
                  </div>
                )}
                <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
                  {submitting ? 'Sending…' : t('landingContact.form.send', { defaultValue: 'Send' })}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </section>
      </div>

      {/* Final CTA — same voice as the rest of the marketing surface. */}
      <section className="mt-16">
        <div className="flex flex-col items-start gap-6 border-t border-[var(--color-border)] pt-12 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
              Or just open an account
            </p>
            <p className="mt-3 max-w-xl font-display text-3xl uppercase leading-tight text-[var(--color-text)]">
              No contact needed. The product is self-serve.
            </p>
          </div>
          <Link to="/register" className="btn btn-primary whitespace-nowrap">
            Create your account
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
